import React, { useState } from "react";
import { toast } from "react-toastify";
import * as hrmsService from "../../services/hrmsService";
import AppDropdown from "../../components/AppDropdown/AppDropdown";
import { HR_PERMISSIONS, hasHrPermission } from "../../constants/hrPermissions";
import LeaveMy, { BalanceCards } from "./LeaveMy";
import LeaveRequestDrawer from "./LeaveRequestDrawer";
import EmployeeSelect from "./EmployeeSelect";
import { EmptyState, Field, LoadState, PageHeader, PrimaryButton, Tabs } from "./hrComponents";
import { buildCalendarGrid, monthBounds, shiftMonth } from "./leaveCalendar";
import { useHr } from "./HrGate";
import { cardStyle, formatDays, formatRange, inputStyle, statusBadge, statusLabel, tdStyle, thStyle, todayYmd, useLoad } from "./hrUi";

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

// LEV-09: who is away, day by day, for the people the caller may see.
function TeamCalendar() {
  const [month, setMonth] = useState(monthBounds(todayYmd()).from);
  const { from, to } = monthBounds(month);
  const { data, loading, error, reload } = useLoad(() => hrmsService.getLeaveCalendar(from, to), [from, to]);
  const grid = buildCalendarGrid(data || [], from, to);
  const [y, m] = month.split("-").map(Number);
  const cell = { width: 26, minWidth: 26, height: 26, textAlign: "center", fontSize: 11, padding: 0, borderBottom: "1px solid #F3F4F6" };

  return (
    <>
      <div className="d-flex align-items-center gap-2 mb-3">
        <button type="button" className="btn btn-outline-secondary btn-sm" aria-label="Previous month" onClick={() => setMonth(shiftMonth(month, -1))}>
          ‹
        </button>
        <strong style={{ minWidth: 140, textAlign: "center" }}>
          {MONTH_NAMES[m - 1]} {y}
        </strong>
        <button type="button" className="btn btn-outline-secondary btn-sm" aria-label="Next month" onClick={() => setMonth(shiftMonth(month, 1))}>
          ›
        </button>
        <span style={{ fontSize: 12, color: "#6B7280", marginLeft: 8 }}>Striped = waiting for approval</span>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {grid.people.length === 0 ? (
          <EmptyState title="Nobody is away this month" />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", minWidth: "100%" }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, position: "sticky", left: 0, zIndex: 1 }}>Employee</th>
                  {grid.days.map((d) => (
                    <th key={d} style={{ ...cell, background: "#F9FAFB", fontWeight: 600 }}>
                      {Number(d.slice(8))}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {grid.people.map((p) => (
                  <tr key={p.employee_id}>
                    <td style={{ ...tdStyle, position: "sticky", left: 0, background: "#fff", whiteSpace: "nowrap" }}>{p.full_name}</td>
                    {grid.days.map((d) => {
                      const c = p.cells[d];
                      return (
                        <td
                          key={d}
                          title={c ? `${c.type}${c.fraction < 1 ? " (half day)" : ""}${c.pending ? " - waiting for approval" : ""}` : undefined}
                          style={{
                            ...cell,
                            background: c ? (c.pending ? `repeating-linear-gradient(45deg, ${c.color}55, ${c.color}55 4px, #fff 4px, #fff 8px)` : c.color) : "transparent",
                            opacity: c && c.fraction < 1 ? 0.6 : 1,
                          }}
                        />
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
    </>
  );
}

const STATUS_OPTIONS = [
  { label: "All statuses", value: "ALL" },
  ...["PENDING", "APPROVED", "REJECTED", "SENT_BACK", "WITHDRAWN", "CANCELLED"].map((s) => ({ label: statusLabel(s), value: s })),
];

// Requests of the people the caller manages (or everyone, for HR).
function TeamRequests() {
  const [status, setStatus] = useState("PENDING");
  const [openId, setOpenId] = useState(null);
  const { data, loading, error, reload } = useLoad(() => hrmsService.listLeaveRequests({ status: status === "ALL" ? undefined : status }), [status]);
  const rows = data || [];
  return (
    <>
      <div style={{ maxWidth: 220 }} className="mb-3">
        <AppDropdown value={status} options={STATUS_OPTIONS} onChange={(e) => setStatus(e.value)} />
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.length === 0 ? (
          <EmptyState title="No leave requests" />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0" style={{ minWidth: 620 }}>
              <thead>
                <tr>
                  {["Employee", "Type", "Dates", "Days", "Status", ""].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600 }}>{r.full_name}</div>
                      <div style={{ fontSize: 12, color: "#6B7280" }}>{r.employee_code}</div>
                    </td>
                    <td style={tdStyle}>{r.leave_type_name}</td>
                    <td style={tdStyle}>{formatRange(r.from_date, r.to_date)}</td>
                    <td style={tdStyle}>{Number(r.total_days)}</td>
                    <td style={tdStyle}>
                      <span style={statusBadge(r.status)}>{statusLabel(r.status)}</span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      <button type="button" className="btn btn-link btn-sm" onClick={() => setOpenId(r.id)}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
      <LeaveRequestDrawer requestId={openId} onClose={() => setOpenId(null)} onChanged={reload} onResubmit={() => {}} />
    </>
  );
}

// HR: correct a balance by hand. The reason is required and every change is in the ledger.
function AdjustBalances() {
  const [employee, setEmployee] = useState(null);
  const [typeId, setTypeId] = useState(null);
  const [days, setDays] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const balances = useLoad(() => hrmsService.getLeaveBalance({ employee_id: employee }), [employee], { enabled: !!employee });
  const list = balances.data?.balances || [];

  const save = async () => {
    setSaving(true);
    try {
      const res = await hrmsService.adjustLeave({ employee_id: employee, leave_type_id: typeId, days: Number(days), reason: reason.trim() });
      if (res?.success) {
        toast.success(`${res.message}. New balance ${formatDays(res.data.balance)}`);
        setDays("");
        setReason("");
        balances.reload();
      } else toast.error(hrmsService.responseMessage(res));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 560 }}>
      <Field label="Employee" required>
        <EmployeeSelect value={employee} onChange={(id) => { setEmployee(id); setTypeId(null); }} />
      </Field>
      {employee ? (
        <LoadState loading={balances.loading} error={balances.error} onRetry={balances.reload} rows={2}>
          <div className="mb-3">
            <BalanceCards balances={list} />
          </div>
          <Field label="Leave type" required>
            <AppDropdown value={typeId} options={list.map((b) => ({ label: b.name, value: b.leave_type_id }))} onChange={(e) => setTypeId(e.value)} placeholder="Select leave type" />
          </Field>
          <Field label="Days to add (use a minus sign to remove)" required>
            <input type="number" step="0.5" className="form-control" style={inputStyle} value={days} onChange={(e) => setDays(e.target.value)} />
          </Field>
          <Field label="Reason" required>
            <textarea className="form-control" rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          <PrimaryButton onClick={save} disabled={saving || !typeId || !Number(days) || !reason.trim()}>
            Save adjustment
          </PrimaryButton>
        </LoadState>
      ) : null}
    </div>
  );
}

export default function Leave() {
  const { access } = useHr();
  const isAdmin = hasHrPermission(access, HR_PERMISSIONS.HR_ADMIN);
  const [tab, setTab] = useState(access.is_employee ? "my" : "calendar");
  const tabs = [
    ...(access.is_employee ? [{ key: "my", label: "My leave" }] : []),
    { key: "calendar", label: "Team calendar" },
    { key: "requests", label: "Team requests" },
    ...(isAdmin ? [{ key: "adjust", label: "Adjust balances" }] : []),
  ];
  return (
    <div style={{ padding: "20px 24px", maxWidth: 1280, margin: "0 auto" }}>
      <PageHeader title="Leave" subtitle="Apply, track and plan time off" />
      <Tabs tabs={tabs} active={tab} onChange={setTab} />
      {tab === "my" ? <LeaveMy /> : null}
      {tab === "calendar" ? <TeamCalendar /> : null}
      {tab === "requests" ? <TeamRequests /> : null}
      {tab === "adjust" ? <AdjustBalances /> : null}
    </div>
  );
}
